import { useRef, useState, useCallback, type DragEvent } from 'react';
import { useUploadManager } from '../../hooks/useUploadManager';
import { type UploadToken, formatBytes, formatBytesToGB } from '../../lib/uploadTypes';
import './UploadPortal.css';

interface UploadPortalProps {
  token: UploadToken;
  tokenString: string;
}

export default function UploadPortal({ token, tokenString }: UploadPortalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    files,
    addFiles,
    removeFile,
    retryFile,
    startUpload,
    cancelAll,
    reset,
    notes,
    setNotes,
    isUploading,
    remainingQuota,
    totalProgress,
    totalSize,
    uploadedSize,
  } = useUploadManager({
    token,
    tokenString,
    onComplete: () => {
      setShowConfirmation(true);
    },
    onError: error => {
      console.error('Upload error:', error);
    },
  });

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        addFiles(droppedFiles);
      }
    },
    [addFiles]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      if (selectedFiles.length > 0) {
        addFiles(selectedFiles);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [addFiles]
  );

  const handleFolderSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      if (selectedFiles.length > 0) {
        addFiles(selectedFiles);
      }
      // Reset input
      if (folderInputRef.current) {
        folderInputRef.current.value = '';
      }
    },
    [addFiles]
  );

  const handleStartUpload = useCallback(() => {
    startUpload();
  }, [startUpload]);

  const handleNewUpload = useCallback(() => {
    reset();
    setShowConfirmation(false);
  }, [reset]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return '✓';
      case 'error':
        return '✗';
      case 'uploading':
        return '⟳';
      case 'cancelled':
        return '⊘';
      default:
        return '○';
    }
  };

  const getStatusClass = (status: string) => {
    return `file-status file-status-${status}`;
  };

  if (showConfirmation) {
    return (
      <div className="upload-portal">
        <div className="upload-confirmation">
          <h2>Upload Complete!</h2>
          <p>
            Your files have been uploaded successfully. You can close this page; uploads will
            continue in the background if any are still processing.
          </p>
          <div className="confirmation-stats">
            <div className="stat">
              <span className="stat-label">Files uploaded:</span>
              <span className="stat-value">
                {files.filter(f => f.status === 'complete').length} / {files.length}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Total size:</span>
              <span className="stat-value">{formatBytes(uploadedSize)}</span>
            </div>
          </div>
          <button className="btn-primary" onClick={handleNewUpload}>
            Upload More Files
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-portal">
      <div className="upload-header">
        <h1>Upload Files</h1>
        {token.clientLabel && <p className="client-label">Project: {token.clientLabel}</p>}
        <div className="quota-display">
          <span className="quota-label">Remaining quota:</span>
          <span className="quota-value">
            {formatBytesToGB(remainingQuota)} GB of {formatBytesToGB(token.limits.maxTotalBytes)} GB
          </span>
        </div>
      </div>

      {files.length === 0 ? (
        <div
          className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="dropzone-content">
            <div className="dropzone-icon">📁</div>
            <h2>Drag & Drop Files Here</h2>
            <p>or</p>
            <div className="dropzone-buttons">
              <button className="btn-secondary" onClick={() => fileInputRef.current?.click()}>
                Choose Files
              </button>
              <button className="btn-secondary" onClick={() => folderInputRef.current?.click()}>
                Choose Folder
              </button>
            </div>
            <p className="dropzone-hint">Supported: Images, Videos, Audio, Text files</p>
          </div>
        </div>
      ) : (
        <div className="upload-queue">
          <div className="queue-header">
            <h2>Upload Queue ({files.length} files)</h2>
            {!isUploading && files.some(f => f.status === 'pending') && (
              <button className="btn-primary" onClick={handleStartUpload}>
                Start Upload
              </button>
            )}
            {isUploading && (
              <button className="btn-secondary" onClick={cancelAll}>
                Cancel All
              </button>
            )}
          </div>

          <div className="total-progress">
            <div className="progress-bar-container">
              <div className="progress-bar" style={{ width: `${totalProgress}%` }} />
            </div>
            <div className="progress-text">
              {formatBytes(uploadedSize)} / {formatBytes(totalSize)} ({totalProgress.toFixed(1)}%)
            </div>
          </div>

          <div className="files-list">
            {files.map(file => (
              <div key={file.id} className="file-item">
                <div className="file-info">
                  <span className={getStatusClass(file.status)}>{getStatusIcon(file.status)}</span>
                  <div className="file-details">
                    <div className="file-name">{file.file.name}</div>
                    <div className="file-meta">
                      {formatBytes(file.file.size)}
                      {file.status === 'uploading' && (
                        <span className="file-progress-text"> - {file.progress.toFixed(1)}%</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="file-actions">
                  {file.status === 'uploading' && (
                    <div className="file-progress-bar">
                      <div className="file-progress-fill" style={{ width: `${file.progress}%` }} />
                    </div>
                  )}
                  {file.status === 'error' && (
                    <button className="btn-retry" onClick={() => retryFile(file.id)}>
                      Retry
                    </button>
                  )}
                  {(file.status === 'pending' ||
                    file.status === 'error' ||
                    file.status === 'cancelled') && (
                    <button className="btn-remove" onClick={() => removeFile(file.id)}>
                      Remove
                    </button>
                  )}
                </div>
                {file.error && <div className="file-error">{file.error}</div>}
              </div>
            ))}
          </div>

          <div className="notes-section">
            <label htmlFor="notes">Notes for editor (optional):</label>
            <textarea
              id="notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add any notes or instructions for the editor..."
              rows={4}
            />
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <input
        ref={folderInputRef}
        type="file"
        {...({ webkitdirectory: '' } as any)}
        multiple
        onChange={handleFolderSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
}
